<script>
import LayoutProvider from "@/components/layouts/LayoutProvider.vue";
import { AppCookieBanner } from "@/components/app/index.js";
import { BModal } from "bootstrap-vue";
import { DateTime } from "luxon";
import { common } from "@/services/index";
import CmpAlert from '@/mixins/CmpAlert.js';

/**
 * Main App
 */
export default {
    name: "MainApp",

    components: {
        LayoutProvider,
        BModal,
        AppCookieBanner,
    },
    mixins: [CmpAlert],
    data() {
        const timeoutInMs = 1800000; // 30 minutes = .5 * 60 * 60 * 1000
        const timeoutInSec = timeoutInMs / 1000; // timeoutInMs in seconds
        
        // this variable contains route paths/names that has table on it;
        const baseRouteTables = [
            'Products',
            'PinCode',
            'Promos',
            'Ads',
            'ServiceCenter',
            'Customers',
            'Admin',
            'Cmp',
            'NonCmp',
            'ProductRepairs',
            'Warranty',
        ];
        const routeTables = [
            'Products',
            'PinCode',
            'Promos',
            'Ads',
            'ServiceCenter',
            'Customers',
            'Admin',
            'Cmp',
            'NonCmp',
            'ProductRepairs',
            'Warranty',
            // add the others

            // details pages
            'Product',
            'ProductsFormEdit',

            'PinCodeFormEdit',
            'Promo',
            'PromoFormEdit',
            'AdvertisementView',
            'AdvertisementFormEdit',
            'ServiceCenterView',
            'ServiceCenterEdit',
            'CustomerProfile',
            'CustomerFormEdit',
            'AdminProfile',
            'AdminFormEdit',
            'CmpView',
            'NonCmpView',
            'ProductRepair',
            'WarrantyView'
            // todo add other details page
        ];
        return {
            buildVersion: "CPA5640000000102",
            storageVersion: 1,
            timeoutInMs: timeoutInMs,
            timeoutInSec: timeoutInSec,
            timeoutCounter: timeoutInSec,
            intervalId: null,
            baseRouteTables: baseRouteTables,
            routeTables: routeTables,
        };
    },

    watch: {
        $route(to, from) {
            /**
             * Closes AppAlerts when route changes
             * Close only error alert
             */
            if (this.$store.getters["alert/type"] === "danger")
                this.$store.commit("alert/error", "");

            /**
             * Save URL
             */
            this.saveUrl();

            this.checkRouterForTables(to, from);
        },

        timeoutCounter(val) {
            const authToken = localStorage.getItem("auth_token");
            if (authToken && val === 10)
                this.$root.$emit("bv::show::modal", "modal-inactive");
        },
    },

    methods: {
        /**
         * to check in table's current page
         */
        checkRouterForTables(to, from) {
            // console.log("this.$route", this.$route)
            if (this.routeTables.includes(this.$route.name)) {
                // console.log("have table");

                let tableRoutePath = localStorage.getItem('table-route-name');
                if (tableRoutePath && this.baseRouteTables.includes(this.$route.name)) {
                    if (tableRoutePath == this.$route.name) {
                        // console.log("dont clear")
                    } else {
                        // console.log("clear")
                        this.removeLocalStorageItems();
                    }
                }
            } else {
                // console.log("no table");
                this.removeLocalStorageItems();
            }
        },
        removeLocalStorageItems() {
            localStorage.removeItem('table-route-full-path');
            localStorage.removeItem('current-tab-index');
            localStorage.removeItem('table-route-name');
            localStorage.removeItem('current-table-page');
        },
        /**
         * Save complete URL to BE for saving when route/page change
         */
        async saveUrl() {
            /**
             * Skips auth routes
             */
            const currentUrl = window.location.href;
            for (let val of ["login", "forgot-password", "reset-password"]) {
                if (currentUrl.includes(val)) return;
            }

            try {
                const response = await common.saveUrl();
            } catch (error) {}
        },

        /**
         * Reference for production build version to help in debugging
         */
        checkBuildVersion() {
            const buildVersion = JSON.parse(
                localStorage.getItem("build_version")
            );
            if (!buildVersion || buildVersion !== this.buildVersion) {
                localStorage.setItem(
                    "build_version",
                    JSON.stringify(this.buildVersion)
                );
            }
        },

        /**
         * Set and update local storage version
         */
        checkLocalStorageVersion() {
            const localStorageVersion = JSON.parse(
                localStorage.getItem("storage_version")
            );
            if (!localStorageVersion) {
                localStorage.setItem(
                    "storage_version",
                    JSON.stringify(this.storageVersion)
                );
            } else if (this.storageVersion !== localStorageVersion) {
                localStorage.clear();
                window.location.reload();
            }
        },

        /**
         * Listens to user movement for idle detection
         */
        handleIdleTimeout() {
            const activeEvents = [
                "load",
                "mousemove",
                "mousedown",
                "click",
                "scroll",
                "keypress",
            ];

            this.timeoutCounter = this.timeoutInSec;
            activeEvents.forEach((event) => {
                window.addEventListener(event, this.resetInactiveTimer);
            });

            this.startInactiveTimer();
        },

        /**
         * Starts idle countdown and resets session_timeout cookiie
         */
        startInactiveTimer() {
            if (this.intervalId) return;

            this.intervalId = setInterval(() => {
                const authToken = localStorage.getItem("auth_token");
                if (!authToken) return;

                if (this.timeoutCounter > 0) this.timeoutCounter--;
                else {
                    this.userLogout();
                }

                // Reset cookie expiry every 5 minutes
                if (this.timeoutCounter % 5 === 0) {
					//   Note: cookie session_timeout replaced to localstorage
                    // this.$cookies.set("session_timeout", DateTime.now(), "4h");

					localStorage.setItem('session_timeout', DateTime.now());
					localStorage.setItem('session_timeout_expiry', '4h');
					if (!localStorage.getItem('session_timeout_started')) {
						localStorage.setItem('session_timeout_started', DateTime.now());
					} else {
						// Check whether session_timeout_started has expired. If it expires, delete session_timeout.
						let session_timeout_started = localStorage.getItem('session_timeout_started');
						if (this.isFourHoursOld(session_timeout_started)) {
							localStorage.removeItem('session_timeout');
						} else {}
					}
                    // console.log("[timeout-counter]", this.timeoutCounter);
                }
            }, 1000);
        },

        /**
         * Hides idle alert and resets timeoutCounter
         */
        resetInactiveTimer() {
            this.$root.$emit("bv::hide::modal", "modal-inactive");
            this.timeoutCounter = this.timeoutInSec;
            this.startInactiveTimer();
        },

        /**
         * Creates a cookie that expires on certain time
         */
        handleSessionTimeout() {
            const authToken = localStorage.getItem("auth_token");

            if (authToken)
                // if (!this.$cookies.isKey("session_timeout")) {
                //     this.userLogout();
                // }

                if (!localStorage.getItem('session_timeout')) {
                    this.userLogout();
                }
        },

        /**
         * Logout
         */
        userLogout() {
            const authToken = localStorage.getItem("auth_token");
            if (authToken) {
                localStorage.removeItem("auth_token");
                localStorage.removeItem("user");
                // this.$cookies.remove("session_timeout");
				localStorage.removeItem('session_timeout');
				localStorage.removeItem('session_timeout_expiry');
				localStorage.removeItem('session_timeout_start_at');
				localStorage.removeItem('session_timeout_started');
				localStorage.setItem('is_logout', '1')
                window.location.reload();
            }
        },

		isFourHoursOld(dateTimeString) {
			// Parse the given date-time string to a Date object
			const givenDateTime = new Date(dateTimeString);

			// Get the current date-time
			const currentDateTime = new Date();

			// Calculate the difference in milliseconds
			const diffInMilliseconds = currentDateTime - givenDateTime;

			// Convert the difference to hours
			const diffInHours = diffInMilliseconds / (1000 * 60 * 60);

			// Check if the difference is greater than or equal to 4 hours
			return diffInHours >= 4;
		}
    },

    created() {
		// remove session_timeout when over 4h old
		if (localStorage.getItem('session_timeout_start_at')) {
			let session_timeout_start_at = localStorage.getItem('session_timeout_start_at');
			if (this.isFourHoursOld(session_timeout_start_at)) {
				localStorage.removeItem('session_timeout');
			}
		}

		if (this.$cookies.isKey("session_timeout")) {
            this.$cookies.remove("session_timeout");
		}
		localStorage.removeItem('session_timeout_started');

        this.checkBuildVersion();
        this.checkLocalStorageVersion();
        this.handleSessionTimeout();
        this.handleIdleTimeout();
    },
};
</script>

<template>
    <LayoutProvider>
        <router-view></router-view>
        <AppCookieBanner />
        <b-modal
            id="modal-inactive"
            centered
            title="Session Timeout"
            title-class="font-size-22 fw-semibold"
            content-class="p-1"
            hide-footer
            hide-header-close
        >
            <p>
                Due to inactivity, your session is about to expire. For your
                security, you will be logged off automatically in &nbsp;<span
                    class="fw-bold"
                    >{{ timeoutCounter }}</span
                >
                seconds.
            </p>
            <p>Press any key to stay logged in.</p>
        </b-modal>
    </LayoutProvider>
</template>

<style lang="scss">
@import "~@/assets/scss/app.scss";

/* Utility */
.select-none {
    user-select: none;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
}

.cursor-pointer {
    cursor: pointer;
}

.cursor-wait {
    cursor: wait;
}

.shadow-1 {
    box-shadow: 0px 3px 6px #d6d6d6;
}

.min-h-65 {
    min-height: 65vh;
}

.min-h-30 {
    min-height: 30vh;
}

.object-cover {
    object-fit: cover;
}

.object-contain {
    object-fit: contain;
}

.border-gray-faded {
    border-color: #ced4da;
}

.border-transparent {
    border: transparent;
}

.custom-ck-editor {
    &.is-invalid {
        .ck {
            &.ck-editor {
                border: 1px solid #fb1f1f !important;
                border-radius: 3px;
            }
            &.ck-focused {
                border: 1px solid #dedede !important;
            }
        }
    }
}

.multiselect {
    &.is-invalid {
        .multiselect__tags {
            border: 1px solid #fb1f1f !important;
        }
    }
}

/* Spacing */
.tracking-wide {
    letter-spacing: 0.025em;
}

.tracking-wider {
    letter-spacing: 0.05em;
}

.tracking-widest {
    letter-spacing: 0.1em;
}

/* Font Size */
.font-size-25 {
    font-size: 25px;
}

/* Bootstrap */
.invalid-feedback {
    span {
        color: #fb1f1f;

        &::before {
            content: "";
            background-image: url("/assets/svg/triangle-exclamation.svg");
            background-repeat: no-repeat;
            background-size: 100% 100%;
            padding-right: 14px;
            padding-bottom: 1px;
            margin-right: 7px;
        }
    }
}

.form-control {
    &.is-invalid {
        border-color: #fb1f1f;
        background-image: none;
    }
    &.is-valid {
        background-image: none;
    }
}

.modal-close-disabled {
    .modal-header .close {
        pointer-events: none !important;
    }
}

/* Colors */
.text-gray-light {
    color: #adb5bd;
}
.text-gray-dark {
    color: #74788d;
}
.text-gray-darker {
    color: #495057;
}
.text-status-green {
    color: #23d440;
}
.text-status-blue {
    color: #4480ae;
}
.text-status-yellow {
    color: #ffd736;
}
.text-status-red {
    color: #fb1f1f;
}
.bg-gray-faded {
    background-color: #ced4da;
}
.bg-red-pale {
    background-color: #fbeeef;
}
.bg-red-faded {
    background-color: #ffeeee;
}
.bg-status-green {
    background-color: #23d440;
}
.bg-status-blue {
    background-color: #4480ae;
}
.bg-status-yellow {
    background-color: #ffd736;
}
.bg-status-red {
    background-color: #fb1f1f;
}

/* Template */
.disabled-input {
    label {
        color: #adb5bd;
    }

    input {
        // margin-top: 0.25rem !important;
        background-color: #fcfcfc;
        border: none;
        color: #495057;
    }

    input::placeholder {
        color: transparent;
    }

    &.disabled-text {
        input {
            color: #adb5bd;
        }
    }
}

/* HTML Tags */

// CRA-200 Input fields should NOT be highlighted blue
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px #ffffff inset !important;
}

/* Custom */
.status {
    &-green {
        background-color: #e7f7d2;
        color: #23d440;
    }
    &-red {
        background-color: #f9dada;
        color: #fb1f1f;
    }
    &-yellow {
        background-color: #fcf4d1;
        color: #ffd736;
    }
    &-blue {
        background-color: #cde8f5;
        color: #4c86b2;
    }
    &-warning {
        background-color: #ffd736;
        color: #ffffff;
    }
}

.custom-invalid-feedback {
    width: 100%;
    margin-top: 0.25rem;
    font-size: 12px;
    color: #f46a6a;

    span {
        color: #fb1f1f;

        &::before {
            content: "";
            background-image: url("/assets/svg/triangle-exclamation.svg");
            background-repeat: no-repeat;
            background-size: 100% 100%;
            padding-right: 14px;
            padding-bottom: 1px;
            margin-right: 7px;
        }
    }
}

.page-marketing {
    header {
        height: 95px;
    }
    .header {
        z-index: 3;
    }
    .wrapper {
        padding-top: 4rem;
    }
}
@media (min-width: 1200px) {
    .page-marketing {
        .wrapper {
            .container {
                max-width: 75vw;
            }
        }
    }
}
@media (min-width: 992px) and (max-width: 1199.98px) {
    .page-marketing {
        .wrapper {
            .container {
                max-width: 90vw;
            }
        }
    }
}
@media (max-width: 991.98px) {
    .page-marketing {
        .wrapper {
            padding-top: 6rem;
            padding-bottom: 6rem;

            .container {
                max-width: 100vw;
            }
        }
    }
}

/** Date picker override */
.mx-calendar-content {
    .cell {
        &.active {
            background-color: #d6001b;
        }
        &.in-range {
            background-color: #ffeeee;
        }
    }
}

.mx-time-column .mx-time-item.active {
    color: #d6001b;
}

.mx-table-date {
    .today {
        color: #d6001b;
    }
}
/** Datepicker invalid input */
.mx-datepicker {
    &.is-invalid {
        .mx-input {
            border: 1px solid #fb1f1f !important;
        }
    }
}

/** Multi Select Place Holder Color */
.multiselect__placeholder {
    color: #74788d !important;
}
.multiselect--disabled,
.multiselect__select {
    background: none !important;
}
/** CRA-1350 */
.multiselect__select {
    z-index: 1 !important;
}

.upload-box.is-invalid {
    border: 2px dashed #fb1f1f !important;
}

/** CRA-1469 Table Clickable Row */
.table {
    .tr {
        &-clickable {
            position: relative !important;

            .td-action > *:not(.tr-link) {
                position: relative;
                z-index: 1;
            }
        }
        &-link {
            position: absolute;
            opacity: 50%;
            height: 100%;
            top: 0;
            left: 0;
            width: 100%;
        }
    }
}
</style>
